/* global globalThis */
/** Opt-in, local-only profiling. Never serialize frames, URLs or player/device identifiers. */
const SAMPLE_MS = 1000, RTC_MS = 5000;
const SOURCE_FIELDS = new Set(("round mode phase preview running armed loop loaded delegate loading switching " +
  "streams videoTracks audioTracks width height frameRate readyState muted enabled pending frames bytes " +
  "callbacks timers intervals subscriptions checking context voices scopes status queued waiters " +
  "localWidth localHeight remoteWidth remoteHeight localFrames remoteFrames localDropped remoteDropped").split(" "));
const TEXT_VALUES = new Set(("BLINK MOUTH GPU CPU rvfc raf intro instruction calibration playing processing " +
  "postround ended live closed new connecting connected disconnected failed idle open reconnecting " +
  "suspended running interrupted loading ready error unknown").split(" "));
const METRICS = new Set(["detection.frame", "detection.inference", "detection.gap", "detection.errors",
  "recording.copy", "recording.encodeLatency", "recording.skippedPending", "recording.errors", "longTask"]);
const KINDS = new Set(["camera", "model", "detector", "recorder", "game", "postround", "audio", "realtime"]);
const EVENTS = new Set(["start", "stop", "hidden", "visible", "pagehide", "pageshow", "lag", "round", "delegate"]);
const SCREENS = new Set(["main", "agreement", "room", "search", "game", "result", "replay", "reward",
  "leaderboard", "profile", "rules", "critical", "diagnostics"]);
const number = (value) => typeof value === "number" && Number.isFinite(value) ? Math.round(value * 100) / 100 : null;

function safeFields(value) {
  const result = {};
  for (const [key, item] of Object.entries(value || {})) {
    if (!SOURCE_FIELDS.has(key)) continue;
    result[key] = typeof item === "boolean" ? item : typeof item === "string"
      ? (TEXT_VALUES.has(item) ? item : "unknown") : number(item);
  }
  return result;
}

/** Raw RTC stats can contain IP addresses, URLs and identifiers. Export only these fields. */
export function summarizeRtc(report) {
  const streams = [];
  report.forEach((stat) => {
    if (streams.length >= 4 || !["outbound-rtp", "inbound-rtp"].includes(stat.type) ||
        (stat.kind || stat.mediaType) !== "video" || stat.isRemote) return;
    const codec = report.get(stat.codecId);
    const item = { direction: stat.type === "outbound-rtp" ? "outbound" : "inbound" };
    for (const key of ["framesPerSecond", "frameWidth", "frameHeight", "framesEncoded", "framesDecoded",
      "framesSent", "framesReceived", "framesDropped", "bytesSent", "bytesReceived", "totalEncodeTime",
      "totalDecodeTime", "packetsLost", "jitter", "freezeCount", "totalFreezesDuration", "qpSum"]) {
      item[key] = number(stat[key]);
    }
    item.codec = /^video\/[a-z0-9.-]{1,20}$/i.test(codec?.mimeType || "") ? codec.mimeType : null;
    item.qualityLimitationReason = ["none", "cpu", "bandwidth", "other"].includes(stat.qualityLimitationReason)
      ? stat.qualityLimitationReason : null;
    item.powerEfficientEncoder = typeof stat.powerEfficientEncoder === "boolean" ? stat.powerEfficientEncoder : null;
    streams.push(item);
  });
  return streams;
}

export function createDiagnostics(host = globalThis, { maxSamples = 1200, maxEvents = 300 } = {}) {
  const sources = new Set(), peers = new Set(), subscribers = new Set();
  let active = false, paused = false, generation = 0, round = 0, screen = "unknown";
  let startedAt = null, stoppedAt = null, startClock = 0, segmentStart = 0, windowStart = 0;
  let samples = [], events = [], droppedSamples = 0, droppedEvents = 0, metadata = null;
  let interval = null, raf = null, observer = null;
  let rafCount = 0, lastRaf = null, maxRafGap = 0, metrics = {};
  const clock = () => host.performance?.now?.() ?? Date.now();
  const wall = () => host.Date?.now?.() ?? Date.now();
  const visible = () => host.document?.visibilityState !== "hidden";
  const notify = () => subscribers.forEach((fn) => { try { fn(); } catch (e) { /* Observational only. */ } });

  function event(type, fields) {
    if (!active || !EVENTS.has(type)) return;
    events.push({ atMs: number(clock() - startClock), type, screen, ...safeFields(fields) });
    if (events.length > maxEvents) { events.shift(); droppedEvents += 1; }
  }

  function metric(name, value) {
    if (!active || paused || !METRICS.has(name) || !Number.isFinite(value) || value < 0) return;
    const bucket = metrics[name] || (metrics[name] = { count: 0, total: 0, max: 0 });
    bucket.count += 1; bucket.total += value; bucket.max = Math.max(bucket.max, value);
  }

  function resetWindow() {
    windowStart = clock(); rafCount = 0; lastRaf = null; maxRafGap = 0; metrics = {};
  }

  function animate(at) {
    raf = null;
    if (!active || paused) return;
    rafCount += 1;
    if (lastRaf !== null) maxRafGap = Math.max(maxRafGap, at - lastRaf);
    lastRaf = at;
    raf = host.requestAnimationFrame(animate);
  }

  function pollPeers() {
    const token = generation;
    peers.forEach((entry) => {
      if (entry.pending || clock() - entry.lastPoll < RTC_MS || typeof entry.pc.getStats !== "function") return;
      entry.pending = true; entry.lastPoll = clock();
      Promise.resolve().then(() => {
        if (!active || paused || generation !== token || !peers.has(entry)) return null;
        return entry.pc.getStats();
      }).then((report) => {
        if (!report || !active || paused || generation !== token || !peers.has(entry)) return;
        entry.latest = { atMs: number(clock() - startClock), streams: summarizeRtc(report) };
      }).catch(() => { /* Unsupported/rejected stats remain null. */ })
        .finally(() => { entry.pending = false; });
    });
  }

  function sample() {
    if (!active || paused) return;
    const at = clock(), duration = at - windowStart;
    if (duration <= 0) return;
    const resources = [], owners = {};
    sources.forEach((entry) => {
      owners[entry.kind] = (owners[entry.kind] || 0) + 1;
      if (resources.length >= 32) return;
      try { resources.push({ kind: entry.kind, ...safeFields(entry.read()) }); }
      catch (e) { resources.push({ kind: entry.kind, unavailable: true }); }
    });
    const measurements = {};
    for (const [key, value] of Object.entries(metrics)) measurements[key] = {
      count: value.count, total: number(value.total), mean: number(value.total / value.count), max: number(value.max),
    };
    samples.push({ atMs: number(at - startClock), windowMs: number(duration), screen, round,
      uiRafFps: typeof host.requestAnimationFrame === "function" ? number(rafCount * 1000 / duration) : null,
      maxUiFrameGapMs: number(maxRafGap), timerDelayMs: number(Math.max(0, duration - SAMPLE_MS)),
      jsHeapBytes: number(host.performance?.memory?.usedJSHeapSize), measurements, owners, resources,
      peerConnections: peers.size, rtc: [...peers].slice(0, 4).map((entry) => entry.latest),
      collectorMs: number(clock() - at) });
    if (samples.length > maxSamples) { samples.shift(); droppedSamples += 1; }
    const previousRaf = lastRaf;
    resetWindow(); lastRaf = previousRaf;
    pollPeers(); notify();
  }

  function suspend(type) {
    if (!active || paused) return;
    sample(); event(type); paused = true; generation += 1;
    host.clearInterval(interval); interval = null;
    if (raf !== null) host.cancelAnimationFrame(raf);
    raf = null; observer?.disconnect(); resetWindow();
  }

  function resume(type) {
    if (!active || !paused || !visible()) return;
    paused = false; segmentStart = clock(); resetWindow(); event(type);
    interval = host.setInterval(sample, SAMPLE_MS);
    if (typeof host.requestAnimationFrame === "function") raf = host.requestAnimationFrame(animate);
    try { observer?.observe({ type: "longtask", buffered: false }); } catch (e) { /* Optional API. */ }
    peers.forEach((entry) => { entry.lastPoll = -Infinity; }); pollPeers();
  }

  const onVisibility = () => visible() ? resume("visible") : suspend("hidden");
  const onPageHide = () => suspend("pagehide");
  const onPageShow = () => resume("pageshow");

  function stop() {
    if (!active) return;
    sample(); event("stop"); active = false; generation += 1; stoppedAt = wall();
    host.clearInterval(interval); interval = null;
    if (raf !== null) host.cancelAnimationFrame(raf);
    raf = null; observer?.disconnect(); observer = null;
    host.document?.removeEventListener("visibilitychange", onVisibility);
    host.removeEventListener?.("pagehide", onPageHide);
    host.removeEventListener?.("pageshow", onPageShow);
    notify();
  }

  function start() {
    if (active) return;
    generation += 1; active = true; paused = true;
    startedAt = wall(); stoppedAt = null; startClock = clock();
    samples = []; events = []; droppedSamples = 0; droppedEvents = 0;
    peers.forEach((entry) => { entry.latest = null; entry.lastPoll = -Infinity; });
    metadata = {
      userAgent: String(host.navigator?.userAgent || "").slice(0, 256),
      hardwareConcurrency: number(host.navigator?.hardwareConcurrency), deviceMemoryGB: number(host.navigator?.deviceMemory),
      screenWidth: number(host.screen?.width), screenHeight: number(host.screen?.height), pixelRatio: number(host.devicePixelRatio),
      mock: process.env.VUE_APP_MOCK_API === "true", devTools: process.env.VUE_APP_DEV_TOOLS === "true",
      capabilities: { longTasks: false, jsHeap: typeof host.performance?.memory?.usedJSHeapSize === "number",
        uiRaf: typeof host.requestAnimationFrame === "function" },
    };
    const Observer = host.PerformanceObserver;
    if (Observer?.supportedEntryTypes?.includes("longtask")) {
      try {
        observer = new Observer((list) => list.getEntries().forEach((entry) => {
          if (entry.startTime >= startClock) metric("longTask", entry.duration);
        }));
        metadata.capabilities.longTasks = true;
      } catch (e) { observer = null; }
    }
    resetWindow(); event("start");
    host.document?.addEventListener("visibilitychange", onVisibility);
    host.addEventListener?.("pagehide", onPageHide); host.addEventListener?.("pageshow", onPageShow);
    resume("visible"); notify();
  }

  return {
    start, stop, sample, metric, event,
    get enabled() { return active; },
    now() { return active && !paused ? clock() : null; },
    elapsed(name, at) { if (at !== null && at >= startClock && at >= segmentStart) metric(name, clock() - at); },
    setScreen(value) { screen = SCREENS.has(value) ? value : "unknown"; },
    nextRound(mode, preview) { round += 1; event("round", { round, mode, preview: !!preview }); return round; },
    registerSource(kind, read) {
      if (!KINDS.has(kind)) return () => {};
      const entry = { kind, read }; sources.add(entry); return () => sources.delete(entry);
    },
    registerPeer(pc) {
      const entry = { pc, pending: false, lastPoll: -Infinity, latest: null };
      peers.add(entry); return () => peers.delete(entry);
    },
    subscribe(fn) { subscribers.add(fn); return () => subscribers.delete(fn); },
    status() { return { enabled: active, hasReport: startedAt !== null, samples: samples.length,
      droppedSamples, round, elapsedSeconds: startedAt === null ? 0 : Math.floor(((stoppedAt ?? wall()) - startedAt) / 1000) }; },
    report() {
      return JSON.parse(JSON.stringify({ schema: "cyberblinking-performance-v1", build: process.env.VUE_APP_BUILD_REVISION || "unknown",
        builtAt: process.env.VUE_APP_BUILD_TIME || null,
        startedAt: startedAt === null ? null : new Date(startedAt).toISOString(),
        stoppedAt: stoppedAt === null ? null : new Date(stoppedAt).toISOString(),
        exportedAt: new Date(wall()).toISOString(), active, metadata,
        limits: { sampleIntervalMs: SAMPLE_MS, rtcIntervalMs: RTC_MS, maxSamples, maxEvents, droppedSamples, droppedEvents },
        notes: ["uiRafFps measures UI callbacks, not camera FPS", "null means unavailable, never zero usage",
          "durations are milliseconds except RTC totalEncodeTime/totalDecodeTime/jitter/totalFreezesDuration (seconds)",
          "encodeLatency includes asynchronous scheduling, not CPU time", "CPU/GPU percentages and temperature are not measured",
          "jsHeapBytes excludes native video/GPU/WASM allocations; no leak conclusion from this alone"],
        samples, events }));
    },
  };
}

export default createDiagnostics(typeof window === "undefined" ? globalThis : window);
